import React, { useState } from "react";

import type { ValidateErrorEntity } from "rc-field-form/lib/interface";

import { getUserSettings, signinUser, updateUserSetting } from "services";

import {
  clearLocalKlimVar,
  clearPersistedQueryCache,
  clearStoredFarmLocations,
  getLocalKlimVar,
  incompleteMessage,
  mapAppTypeToString,
  setLocalKlimVar,
  useAppType,
  useGetFarmerType,
  useLocation,
  useNavigate,
  useToggleNavs,
  useTranslation,
} from "utils";

import { SOIL_SIGHT_FLOW_ROUTES } from "consts";

import { ErrorBoundary, Flex, Form, MainPage, ScreenSplit, Typography, Message as message } from "components";

import { SignInButtons } from "pages/app/account/components/SignInButtons";
import { SignInInputs } from "pages/app/account/components/SignInInputs";
import { TestimonialScreen } from "pages/app/account/components/TestimonialScreen";

interface SignInFormValues {
  email: string;
  password: string;
}

interface ShareData {
  title: string;
  summary: string;
  link: string;
  image: string;
}

interface SignInResponse {
  token: string;
}

interface UserSettingsResponse {
  appAlertHidden?: boolean;
  referralAlert?: number;
}

export const SignIn = () => {
  const { t } = useTranslation(["signinup", "meta"]);

  const [loading, setLoading] = useState<boolean>(false);
  const appType = useAppType();
  const navigate = useNavigate();
  const location = useLocation();
  const farmerType = useGetFarmerType();
  // The funnel's log-in action sets this. It rides one history entry, so we never have to clear it.
  const isFromSoilSightFunnel = (location.state as { fromSoilSight?: boolean } | null)?.fromSoilSight === true;
  useToggleNavs(false, appType.mobile);

  const [share] = useState<ShareData>({
    title: t("meta:signIn.title"),
    summary: t("meta:signIn.description"),
    link: `${process.env.REACT_APP_FARM_URL}/sign-in`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });

  const completeSignin = (res: SignInResponse): void => {
    // A session that ended by token expiry rather than via /sign-out leaves its cache in place, so
    // clear before the new token lands to stop the previous user's data being served to this one.
    clearPersistedQueryCache();
    clearStoredFarmLocations();
    setLocalKlimVar("token", res.token);
    const urlSlice = getLocalKlimVar("urlSlice");
    const controller = new AbortController();

    getUserSettings({
      signal: controller.signal,
    })
      .then((res) => {
        if (!res) {
          return;
        }

        const settings = res as unknown as UserSettingsResponse;

        if (appType.desktop || settings.appAlertHidden) {
          const currentAlert = settings.referralAlert || 0;
          updateUserSetting({
            item: "referralAlert",
            value: currentAlert < 4 ? currentAlert + 1 : currentAlert,
          });
        }
      })
      .finally(() => {
        message({ content: t("signin.success") });
        if (urlSlice) {
          clearLocalKlimVar("urlSlice");
          navigate(`/${urlSlice}`);
        } else if (isFromSoilSightFunnel) {
          navigate(SOIL_SIGHT_FLOW_ROUTES.scenarios);
        } else {
          navigate("/dashboard");
        }
      })
      // The global axios handler already showed the failure. We catch so the rejection does not escape.
      .catch(() => undefined);
  };

  const complete = (v: SignInFormValues): void => {
    const controller = new AbortController();
    setLoading(true);

    signinUser({
      appType: mapAppTypeToString(appType),
      email: v.email,
      password: v.password,
      signal: controller.signal,
    })
      .then(completeSignin)
      .finally(() => {
        setLoading(false);
      })
      // The global axios handler already showed the failure. We catch so the rejection does not escape.
      .catch(() => undefined);
  };

  const incomplete = (v: ValidateErrorEntity<SignInFormValues>): void => {
    if (!v.values.email || !v.values.password) {
      message({
        content: t("errors.mandatoryFields"),
        icon: "error",
      });
      return;
    }

    if (!v.values.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) {
      message({
        content: t("errors.invalidEmailSignIn"),
        icon: "error",
      });
      return;
    }

    incompleteMessage(t("errors.mandatoryFields"));
  };

  return (
    <ScreenSplit sideChildren={<TestimonialScreen farmerType={farmerType ?? "default"} />}>
      <MainPage loading={false} pageName={"sign-in-up"} share={share} containerClass={appType.mobile ? "small" : "margin-top-0"}>
        <Flex vertical gap={32} className="mini-form margin-top-200">
          <Flex vertical gap={8}>
            <Typography variant="h2">{t("signin.title")}</Typography>
            <Typography variant="paragraph-m">{t("signin.description")}</Typography>
          </Flex>
          <Form name="signin" onFinish={(v) => complete(v)} onFinishFailed={incomplete}>
            <Flex vertical gap={32}>
              <ErrorBoundary>
                <SignInInputs />
              </ErrorBoundary>
              <ErrorBoundary>
                <SignInButtons loading={loading} />
              </ErrorBoundary>
            </Flex>
          </Form>
        </Flex>
      </MainPage>
    </ScreenSplit>
  );
};
